import { createLazyLoader } from '../../scripts/lazy-load';

const loadMenuModules = createLazyLoader(() =>
  Promise.all([import('./header-drawer.js'), import('../scripts/header-drawer-breadcrumbs.js')])
);

const loadSearchModules = createLazyLoader(() =>
  Promise.all([import('../scripts/dialog.js'), import('../scripts/predictive-search.js')])
);

/** @param {() => Promise<unknown>} loader */
async function ensureModules(loader) {
  await loader();
}

/**
 * @param {Event} event
 */
function redispatchEvent(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  if (event instanceof MouseEvent) {
    target.dispatchEvent(
      new MouseEvent(event.type, {
        bubbles: true,
        cancelable: true,
        view: window,
        detail: event.detail,
        screenX: event.screenX,
        screenY: event.screenY,
        clientX: event.clientX,
        clientY: event.clientY,
        ctrlKey: event.ctrlKey,
        altKey: event.altKey,
        shiftKey: event.shiftKey,
        metaKey: event.metaKey,
        button: event.button,
        buttons: event.buttons,
      })
    );
    return;
  }

  if (event instanceof KeyboardEvent) {
    // Native <summary>/<button> activation via Enter/Space produces a click, which is
    // what the declarative on:click handlers listen for. A synthetic KeyboardEvent does
    // not reproduce that activation, so re-dispatch a click to open the drawer/search.
    target.dispatchEvent(
      new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
        detail: 1,
      })
    );
  }
}

/**
 * @param {Element} target
 * @param {() => Promise<unknown>} loader
 * @param {string[]} customElementNames
 * @param {Event} event
 */
async function interceptForLazyLoad(target, loader, customElementNames, event) {
  if (customElementNames.every((name) => Boolean(customElements.get(name)))) return false;

  event.preventDefault();
  event.stopImmediatePropagation();

  await ensureModules(loader);
  await Promise.all(customElementNames.map((name) => customElements.whenDefined(name)));

  redispatchEvent(event);
  return true;
}

/** @param {Event} event */
function handleMenuInteraction(event) {
  if (!(event.target instanceof Element)) return;

  const menuTrigger =
    event.target.closest('header-drawer summary') ??
    event.target.closest('[on\\:click="#header-drawer/open"]') ??
    event.target.closest('[on\\:click="#header-drawer/back"]') ??
    event.target.closest('[on\\:click="#header-drawer/close"]');

  if (!menuTrigger) return;

  void interceptForLazyLoad(menuTrigger, loadMenuModules, ['header-drawer'], event);
}

/** @param {Event} event */
function handleSearchInteraction(event) {
  if (!(event.target instanceof Element)) return;

  const searchTrigger =
    event.target.closest('[on\\:click="#search-modal/showDialog"]') ??
    event.target.closest('search-button button');

  if (!searchTrigger) return;

  void interceptForLazyLoad(searchTrigger, loadSearchModules, ['dialog-component', 'predictive-search-component'], event);
}

// Keys that activate a focused <summary>/<button>. Only these should be intercepted on
// keydown — swallowing others (Tab, arrows, …) would break keyboard navigation.
const ACTIVATION_KEYS = new Set(['Enter', ' ', 'Spacebar']);

/** @param {Event} event */
function handleCapturedInteraction(event) {
  if (event instanceof KeyboardEvent && !ACTIVATION_KEYS.has(event.key)) return;

  handleMenuInteraction(event);
  handleSearchInteraction(event);
}

function prefetchMenuOnHover() {
  const headerMenu = document.querySelector('header-menu');
  if (!headerMenu) return;

  headerMenu.addEventListener(
    'pointerenter',
    () => {
      void loadMenuModules();
    },
    { once: true, passive: true }
  );
}

function prefetchMenuOnMobileTriggerHover() {
  const mobileTrigger = document.querySelector('[header-mobile-trigger]');
  if (!mobileTrigger) return;

  mobileTrigger.addEventListener(
    'pointerenter',
    () => {
      void loadMenuModules();
    },
    { once: true, passive: true }
  );
}

function eagerLoadInDesignMode() {
  if (!window.Shopify?.designMode) return;

  void Promise.all([loadMenuModules(), loadSearchModules()]);
}

document.addEventListener('click', handleCapturedInteraction, true);
document.addEventListener('keydown', handleCapturedInteraction, true);

prefetchMenuOnHover();
prefetchMenuOnMobileTriggerHover();
eagerLoadInDesignMode();
